"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.RouteProcessor = void 0;
exports.getRouteProcessorCode = getRouteProcessorCode;
const bignumber_1 = require("@ethersproject/bignumber");
const amm_1 = require("@zenlink-interface/amm");
const tiny_invariant_1 = __importDefault(require("tiny-invariant"));
const CommandCode_1 = require("../CommandCode");
const entities_1 = require("../entities");
const HEXer_1 = require("../HEXer");
const util_1 = require("../util");
function last(arr) {
    return arr[arr.length - 1];
}
class RouteProcessor {
    constructor(routeProcessorAddress, feeSettlementAddress, pools) {
        this.routeProcessorAddress = routeProcessorAddress;
        this.feeSettlementAddress = feeSettlementAddress;
        this.pools = pools;
        this.tokenOutputLegs = new Map();
    }
    getRouteCode(route) {
        // 0. Check for no route
        if (route.status === amm_1.RouteStatus.NoWay || !route.legs.length)
            return '';
        if (route.legs.length === 1 && route.fromToken.address === '') {
            // very special case
            return this.getCodeForsimpleWrap(route);
        }
        this.setTokenOutputLegs(route);
        let res = '0x';
        // 1. Initial distribution
        const [initialCode, exactAmount] = this.getCodeDistributeInitial(route);
        res += initialCode;
        const distributedTokens = new Set([route.fromToken.tokenId]);
        route.legs.forEach((l, i) => {
            if (i === 0 && l.tokenFrom.address === '') {
                // Native - processed by codeDistributeInitial
                distributedTokens.add(l.tokenTo.tokenId);
                return;
            }
            // 2. Transfer tokens from the routeProcessor contract to the pool if it is neccessary
            if (!distributedTokens.has(l.tokenFrom.tokenId)) {
                res += this.getCodeDistributeTokenShares(l.tokenFrom, route);
                distributedTokens.add(l.tokenFrom.tokenId);
            }
            // 3. get pool's output address
            const outAddress = this.getPoolOutputAddress(l, route);
            // 4. Make swap
            res += this.getCodeSwap(l, route, outAddress, exactAmount.get(l.poolAddress));
        });
        return res;
    }
    getCodeForsimpleWrap(route) {
        const hex = new HEXer_1.HEXer()
            // wrapAndDistributeERC20Amounts
            .uint8(CommandCode_1.CommandCode.WRAP_AND_DISTRIBUTE_ERC20_AMOUNTS)
            .address(route.legs[0].poolAddress)
            .uint8(1)
            .address(this.feeSettlementAddress)
            .uint(route.amountInBN);
        return hex.toString0x();
    }
    setTokenOutputLegs(route) {
        const tokenOutputLegs = new Map();
        route.legs.forEach((l) => {
            const tokenId = l.tokenFrom.tokenId?.toString();
            (0, tiny_invariant_1.default)(tokenId !== undefined, 'Unseted tokenId');
            const legsOutput = tokenOutputLegs.get(tokenId) || [];
            legsOutput.push(l);
            tokenOutputLegs.set(tokenId, legsOutput);
        });
        this.tokenOutputLegs = tokenOutputLegs;
    }
    getPoolCode(l) {
        const pc = this.pools.get(l.poolId);
        if (!pc)
            throw new Error(`unknown pool: ${l.poolId}`);
        return pc;
    }
    // Distributes tokens from msg.sender to pools
    getCodeDistributeInitial(route) {
        let fromToken = route.fromToken;
        if (fromToken.address === '') {
            // Native
            fromToken = route.legs[0].tokenTo; // Change to wrapped Native
        }
        const legs = this.tokenOutputLegs.get(fromToken.tokenId);
        const legsAddr = legs.map((l) => {
            const pc = this.getPoolCode(l);
            const startPoint = pc.getStartPoint(l, route);
            return [
                l,
                startPoint === entities_1.PoolCode.RouteProcessorAddress
                    ? this.routeProcessorAddress
                    : startPoint,
            ];
        });
        const hex = new HEXer_1.HEXer();
        if (route.fromToken.address === '') {
            // wrapAndDistributeERC20Amounts
            hex
                .uint8(CommandCode_1.CommandCode.WRAP_AND_DISTRIBUTE_ERC20_AMOUNTS)
                .address(route.legs[0].poolAddress);
        }
        else {
            // distributeERC20Amounts
            hex.uint8(CommandCode_1.CommandCode.DISTRIBUTE_ERC20_AMOUNTS);
        }
        hex.uint8(legsAddr.length);
        let inputAmountPrevious = bignumber_1.BigNumber.from(0);
        const lastLeg = last(legsAddr)[0];
        const exactAmount = new Map();
        legsAddr.forEach(([leg, poolAddress]) => {
            const amount = leg !== lastLeg
                ? (0, util_1.getBigNumber)(route.amountIn * leg.absolutePortion)
                : route.amountInBN.sub(inputAmountPrevious);
            hex.address(poolAddress).uint(amount);
            inputAmountPrevious = inputAmountPrevious.add(amount);
            exactAmount.set(leg.poolAddress, amount);
        });
        const code = hex.toString();
        return [code, exactAmount];
    }
    getCodeDistributeTokenShares(token, route) {
        const legs = this.tokenOutputLegs.get(token.tokenId);
        if (legs.length <= 1)
            return ''; // No distribution is needed
        const startPointsNum = legs.filter((l) => {
            const pc = this.getPoolCode(l);
            const startPoint = pc.getStartPoint(l, route);
            return startPoint === entities_1.PoolCode.RouteProcessorAddress;
        }).length;
        if (startPointsNum > 1)
            throw new Error('More than one input token is not supported by RouteProcessor');
        const hex = new HEXer_1.HEXer()
            .uint8(CommandCode_1.CommandCode.DISTRIBUTE_ERC20_SHARES)
            .address(token.address)
            .uint8(legs.length - startPointsNum);
        let unmovedPart = 0;
        let unmovedCounter = 0;
        legs.forEach((l) => {
            const pc = this.getPoolCode(l);
            const startPoint = pc.getStartPoint(l, route);
            if (startPoint === entities_1.PoolCode.RouteProcessorAddress) {
                unmovedPart += l.swapPortion;
                ++unmovedCounter;
            }
            else {
                const amount = l.swapPortion * (1 - unmovedPart);
                hex.address(startPoint).share16(amount);
            }
        });
        const code = hex.toString();
        (0, tiny_invariant_1.default)(code.length === (22 + (legs.length - unmovedCounter) * 22) * 2, 'codeDistributeTokenShares unexpected code length');
        return code;
    }
    getPoolOutputAddress(l, route) {
        let outAddress;
        const outputDistribution = this.tokenOutputLegs.get(l.tokenTo.tokenId) || [];
        if (!outputDistribution.length) {
            outAddress = this.feeSettlementAddress;
        }
        else if (outputDistribution.length === 1) {
            outAddress = this.getPoolCode(outputDistribution[0]).getStartPoint(l, route);
            if (outAddress === entities_1.PoolCode.RouteProcessorAddress)
                outAddress = this.routeProcessorAddress;
        }
        else {
            outAddress = this.routeProcessorAddress;
        }
        return outAddress;
    }
    getCodeSwap(leg, route, to, exactAmount) {
        const pc = this.getPoolCode(leg);
        return pc.getSwapCodeForRouteProcessor(leg, route, to, exactAmount);
    }
}
exports.RouteProcessor = RouteProcessor;
function getRouteProcessorCode(route, routeProcessorAddress, feeSettlementAddress, pools) {
    const rp = new RouteProcessor(routeProcessorAddress, feeSettlementAddress, pools);
    return rp.getRouteCode(route);
}
